import api from './api/axiosConfig';

/** Unwrap ApiResponse envelope when present: { success, message, data } -> data */
function unwrap(res) {
  const d = res?.data;
  if (d && typeof d.success === 'boolean' && 'data' in d) return d.data;
  return d;
}

function errorMessage(err) {
  const d = err?.response?.data;
  return d?.message || d?.error || 'Request failed';
}

const imageService = {
  async uploadMultipleImages(formData) {
    try {
      const response = await api.post('/images/upload-multiple/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return unwrap(response);
    } catch (error) {
      throw new Error(errorMessage(error) || 'Failed to upload images');
    }
  },

  async getContentImages(contentTypeId, objectId) {
    try {
      const response = await api.get('/images/content-images/', {
        params: {
          content_type_id: contentTypeId,
          object_id: objectId,
        },
      });
      return unwrap(response);
    } catch (error) {
      throw new Error(errorMessage(error) || 'Failed to fetch images');
    }
  },

  // Get all images with filtering
  async getImages(params = {}) {
    try {
      const response = await api.get('/images/', { params });
      return unwrap(response);
    } catch (error) {
      throw new Error(errorMessage(error) || 'Failed to fetch images');
    }
  },

  async getImage(imageId) {
    try {
      const response = await api.get(`/images/${imageId}/`);
      return unwrap(response);
    } catch (error) {
      throw new Error(errorMessage(error) || 'Failed to fetch image');
    }
  },

  async updateImage(imageId, data) {
    try {
      const response = await api.patch(`/images/${imageId}/`, data);
      return unwrap(response);
    } catch (error) {
      throw new Error(errorMessage(error) || 'Failed to update image');
    }
  },

  async deleteImage(imageId) {
    try {
      await api.delete(`/images/${imageId}/`);
      return true;
    } catch (error) {
      throw new Error(errorMessage(error) || 'Failed to delete image');
    }
  },

  async searchImages(query, filters = {}) {
    try {
      const response = await api.get('/images/search/', {
        params: { q: query, ...filters },
      });
      return unwrap(response);
    } catch (error) {
      throw new Error(errorMessage(error) || 'Failed to search images');
    }
  },

  async getCategories() {
    try {
      const response = await api.get('/images/categories/');
      return unwrap(response);
    } catch (error) {
      throw new Error(errorMessage(error) || 'Failed to fetch categories');
    }
  },

  async getTags() {
    try {
      const response = await api.get('/images/tags/');
      return unwrap(response);
    } catch (error) {
      throw new Error(errorMessage(error) || 'Failed to fetch tags');
    }
  },

  async tagImage(imageId, tagName) {
    try {
      const response = await api.post(`/images/${imageId}/tag/`, {
        tag_name: tagName,
      });
      return unwrap(response);
    } catch (error) {
      throw new Error(errorMessage(error) || 'Failed to tag image');
    }
  },

  async untagImage(imageId, tagName) {
    try {
      const response = await api.delete(`/images/${imageId}/untag/`, {
        data: { tag_name: tagName },
      });
      return unwrap(response);
    } catch (error) {
      throw new Error(errorMessage(error) || 'Failed to untag image');
    }
  },

  async getGalleries() {
    try {
      const response = await api.get('/images/galleries/');
      return unwrap(response);
    } catch (error) {
      throw new Error(errorMessage(error) || 'Failed to fetch galleries');
    }
  },

  async createGallery(data) {
    try {
      const response = await api.post('/images/galleries/', data);
      return unwrap(response);
    } catch (error) {
      throw new Error(errorMessage(error) || 'Failed to create gallery');
    }
  },

  async updateGallery(galleryId, data) {
    try {
      const response = await api.patch(`/images/galleries/${galleryId}/`, data);
      return unwrap(response);
    } catch (error) {
      throw new Error(errorMessage(error) || 'Failed to update gallery');
    }
  },

  async deleteGallery(galleryId) {
    try {
      await api.delete(`/images/galleries/${galleryId}/`);
      return true;
    } catch (error) {
      throw new Error(errorMessage(error) || 'Failed to delete gallery');
    }
  },

  async addImageToGallery(galleryId, imageId) {
    try {
      const response = await api.post(`/images/galleries/${galleryId}/images/${imageId}/add/`);
      return unwrap(response);
    } catch (error) {
      throw new Error(errorMessage(error) || 'Failed to add image to gallery');
    }
  },

  async removeImageFromGallery(galleryId, imageId) {
    try {
      const response = await api.delete(`/images/galleries/${galleryId}/images/${imageId}/remove/`);
      return unwrap(response);
    } catch (error) {
      throw new Error(errorMessage(error) || 'Failed to remove image from gallery');
    }
  },
};

export { imageService };
